import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Flame, Check, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useApi, errMsg, money, fmtDate } from "@/lib/api";
import { useMe } from "@/context/Me";
import { PageTitle } from "@/components/bits";

export function Plans() {
  const api = useApi();
  const { me, meta, refresh } = useMe();
  const [busy, setBusy] = useState("");
  if (me?.profile_type !== "pro") return <div className="p-10 text-center text-slate-400">Planos são para profissionais.</div>;
  const pr = meta?.prices?.[me.country] || {};
  const buy = async (product) => {
    setBusy(product);
    try { const { data } = await api.post("/payments/checkout", { product, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  const cancel = async () => { try { await api.post("/payments/cancel-pro"); toast.success("Renovação cancelada. O Pro fica ativo até o fim do período."); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <PageTitle eyebrow="Planos" title="Cresça no Zelobra" />
      <div className={`grid gap-5 ${pr.featured ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        <div className="panel p-6" data-testid="plan-free">
          <div className="eyebrow mb-2 !text-slate-400">Grátis</div>
          <div className="font-display text-5xl font-black">{money(0, me.country)}</div>
          <ul className="mt-5 space-y-2 text-sm text-slate-300">{["3 propostas por mês", "Perfil com fotos e avaliações", "Mural com mapa"].map((x) => <li key={x}><Check size={14} className="mr-2 inline text-emerald-400" />{x}</li>)}</ul>
          {!me.is_pro && <div className="mt-6 rounded-xl bg-white/5 px-4 py-2 text-center text-sm font-bold">Plano atual · {me.proposals_used}/3 usadas</div>}
        </div>
        <div className="panel relative border-purple-500/40 p-6 shadow-[0_0_40px_rgba(168,85,247,0.15)]" data-testid="plan-pro">
          <div className="eyebrow mb-2 flex items-center gap-2 !text-purple-300"><Crown size={14} />Pro mensal</div>
          <div className="font-display text-5xl font-black">{money(pr.pro, me.country)}<span className="text-lg text-slate-400">/mês</span></div>
          <ul className="mt-5 space-y-2 text-sm text-slate-300">{["Propostas ilimitadas", "Selo Pro no perfil e nas propostas", "Prioridade na lista de propostas", "Renova todo mês · cancele quando quiser"].map((x) => <li key={x}><Check size={14} className="mr-2 inline text-purple-300" />{x}</li>)}</ul>
          {me.is_pro ? (
            <div className="mt-6 space-y-2 text-sm">
              <div className="rounded-xl bg-purple-500/15 px-4 py-2 text-center font-bold text-purple-200" data-testid="plan-pro-active">Ativo até {fmtDate(me.pro_until)}</div>
              {!me.pro_cancel_at_period_end ? <button data-testid="plan-pro-cancel" className="w-full text-slate-400 hover:text-rose-400" onClick={cancel}>Cancelar renovação</button> : <p className="text-center text-xs text-slate-500">Renovação cancelada</p>}
            </div>
          ) : <button data-testid="plan-pro-buy" className="btn-hazard mt-6 w-full bg-purple-500 text-white hover:bg-purple-600" disabled={!!busy} onClick={() => buy("pro")}>{busy === "pro" ? <Loader2 className="animate-spin" /> : "Assinar Pro"}</button>}
        </div>
        {pr.featured ? (
        <div className="panel p-6" data-testid="plan-featured">
          <div className="eyebrow mb-2 flex items-center gap-2 !text-orange-300"><Flame size={14} />Destaque 7 dias</div>
            <>
              <div className="font-display text-5xl font-black">{money(pr.featured, me.country)}</div>
              <ul className="mt-5 space-y-2 text-sm text-slate-300">{["Selo Destaque", "Suas propostas aparecem no topo", "Pagamento único"].map((x) => <li key={x}><Check size={14} className="mr-2 inline text-orange-300" />{x}</li>)}</ul>
              {me.is_featured && <div className="mt-4 rounded-xl bg-orange-500/15 px-4 py-2 text-center text-sm font-bold text-orange-200" data-testid="plan-featured-active">Ativo até {fmtDate(me.featured_until)}</div>}
              <button data-testid="plan-featured-buy" className="btn-hazard mt-4 w-full bg-[#FF5722] text-white hover:bg-[#E04818]" disabled={!!busy} onClick={() => buy("featured")}>{busy === "featured" ? <Loader2 className="animate-spin" /> : me.is_featured ? "Estender +7 dias" : "Comprar Destaque"}</button>
            </>
        </div>
        ) : null}
      </div>
      <p className="mt-6 text-xs text-slate-500">Pagamento seguro com cartão pela Stripe. Impostos calculados no checkout quando aplicável.</p>
    </div>
  );
}

export function PaymentSuccess() {
  const api = useApi();
  const { refresh } = useMe();
  const [params] = useSearchParams();
  const [st, setSt] = useState("checking");
  useEffect(() => {
    const sid = params.get("session_id");
    let n = 0; let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setSt("paid"); refresh(); return; }
        if (data.status === "expired") { setSt("failed"); return; }
      } catch {}
      if (++n < 15) setTimeout(poll, 2000); else setSt("timeout");
    };
    poll();
    return () => { stop = true; };
  }, []); // eslint-disable-line
  return (
    <div className="mx-auto max-w-lg px-4 py-20 text-center" data-testid="payment-result">
      {st === "checking" && <><Loader2 className="mx-auto animate-spin text-[#FFC700]" size={48} /><h1 className="mt-4 text-3xl font-black uppercase">Confirmando pagamento…</h1></>}
      {st === "paid" && <><CheckCircle2 className="mx-auto text-emerald-400" size={56} /><h1 className="mt-4 text-4xl font-black uppercase" data-testid="payment-success">Pagamento confirmado!</h1><p className="mt-2 text-slate-400">Seu plano já está ativo.</p></>}
      {(st === "failed" || st === "timeout") && <><XCircle className="mx-auto text-rose-400" size={56} /><h1 className="mt-4 text-3xl font-black uppercase">Não conseguimos confirmar</h1><p className="mt-2 text-slate-400">Se o valor foi cobrado, o plano será ativado em instantes.</p></>}
      <Link to="/planos" className="btn-hazard mt-8" data-testid="payment-back">Voltar aos planos</Link>
    </div>
  );
}

export const PaymentCancel = () => (
  <div className="mx-auto max-w-lg px-4 py-20 text-center"><XCircle className="mx-auto text-slate-400" size={56} /><h1 className="mt-4 text-3xl font-black uppercase">Pagamento cancelado</h1><Link to="/planos" className="btn-hazard mt-8" data-testid="payment-cancel-back">Voltar aos planos</Link></div>
);
